/**
 * FinancialsDashboard Component
 * Financial overview, invoices, payments, payroll, and tax tracking
 */

import React, { useState, useEffect } from 'react';
import { invoiceApi, clientApi } from '../services/api';

interface InvoiceRow {
  id: string;        // invoice number (display)
  dbId?: number;     // server primary key (present when loaded from API)
  client: string;
  amount: number;
  status: string;
  date: string;
}

interface FinancialsDashboardProps {
  onCreateInvoice?: () => void;
}

// Sample fallback data (shown until real invoices load)
const sampleInvoices: InvoiceRow[] = [
  { id: 'INV-2024-0089', client: 'ABC Roofing', amount: 2450, status: 'paid', date: '2024-01-05' },
  { id: 'INV-2024-0088', client: 'XYZ Construction', amount: 3200, status: 'sent', date: '2024-01-03' },
  { id: 'INV-2024-0087', client: 'Pro Contractors', amount: 2100, status: 'overdue', date: '2023-12-20' },
  { id: 'INV-2024-0086', client: 'ABC Roofing', amount: 1850, status: 'draft', date: '2024-01-06' },
];

const FinancialsDashboard: React.FC<FinancialsDashboardProps> = ({ onCreateInvoice }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('month');
  const [invoices, setInvoices] = useState<InvoiceRow[]>(sampleInvoices);
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [viewedInvoice, setViewedInvoice] = useState<InvoiceRow | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);

  // Mock data
  const stats = {
    revenue: { month: 24500, ytd: 287000, growth: 12 },
    outstanding: { amount: 8450, count: 3 },
    overdue: { amount: 2100, count: 1 },
    payroll: { nextRun: '2024-01-15', amount: 12500 }
  };

  // Load real invoices from the API (falls back to sample data on failure)
  useEffect(() => {
    const loadInvoices = async () => {
      const [invRes, clientRes] = await Promise.all([invoiceApi.list(), clientApi.list()]);
      if (invRes.success && invRes.data?.invoices?.length) {
        const clientNames: Record<number, string> = {};
        if (clientRes.success && clientRes.data?.clients) {
          clientRes.data.clients.forEach((c: any) => { clientNames[c.id] = c.name; });
        }
        setInvoices(invRes.data.invoices.map((i: any) => ({
          id: i.invoice_number,
          dbId: i.id,
          client: clientNames[i.client_id] || (i.client_id ? `Client #${i.client_id}` : '-'),
          amount: i.total || 0,
          status: i.status,
          date: i.created_at ? i.created_at.slice(0, 10) : '-',
        })));
      }
    };
    loadInvoices();
  }, []);

  const handleSendInvoice = async (inv: InvoiceRow) => {
    if (inv.dbId == null) return;
    setSendingId(inv.id);
    const res = await invoiceApi.send(inv.dbId);
    if (res.success) {
      setInvoices(prev => prev.map(i => (i.id === inv.id ? { ...i, status: 'sent' } : i)));
    } else {
      alert(res.error || 'Failed to send invoice');
    }
    setSendingId(null);
  };

  const clientOptions = Array.from(new Set(invoices.map(inv => inv.client)));
  const filteredInvoices = invoices.filter(inv =>
    (statusFilter === 'all' || inv.status === statusFilter) &&
    (clientFilter === 'all' || inv.client === clientFilter)
  );

  const taxSummary = {
    federal: { liability: 12500, paid: 9500, due: 3000 },
    state: { liability: 3800, paid: 2800, due: 1000 },
    fica: { liability: 8200, paid: 6200, due: 2000 },
    quarterlyDue: '2024-01-15'
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'var(--success)';
      case 'sent': return 'var(--primary)';
      case 'overdue': return 'var(--danger)';
      case 'draft': return 'var(--secondary)';
      default: return 'var(--secondary)';
    }
  };

  return (
    <div className="financials-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <h2>💰 Financials</h2>
        <div className="header-actions">
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
          <button className="btn primary" onClick={() => onCreateInvoice?.()}>+ Create Invoice</button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card large">
          <div className="stat-header">
            <span className="stat-icon">💵</span>
            <span className="stat-trend up">+{stats.revenue.growth}%</span>
          </div>
          <div className="stat-value">${stats.revenue.month.toLocaleString()}</div>
          <div className="stat-label">Revenue This Month</div>
          <div className="stat-secondary">YTD: ${stats.revenue.ytd.toLocaleString()}</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-value">${stats.outstanding.amount.toLocaleString()}</div>
          <div className="stat-label">Outstanding ({stats.outstanding.count} invoices)</div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">⚠️</div>
          <div className="stat-value">${stats.overdue.amount.toLocaleString()}</div>
          <div className="stat-label">Overdue ({stats.overdue.count} invoice)</div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-value">${stats.payroll.amount.toLocaleString()}</div>
          <div className="stat-label">Next Payroll ({stats.payroll.nextRun})</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-navigation">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Invoices
        </button>
        <button 
          className={`tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          Payments
        </button>
        <button 
          className={`tab-btn ${activeTab === 'payroll' ? 'active' : ''}`}
          onClick={() => setActiveTab('payroll')}
        >
          Payroll
        </button>
        <button 
          className={`tab-btn ${activeTab === 'taxes' ? 'active' : ''}`}
          onClick={() => setActiveTab('taxes')}
        >
          Taxes
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-content">
            <div className="content-grid">
              {/* Recent Invoices */}
              <div className="section-card">
                <div className="section-header">
                  <h3>Recent Invoices</h3>
                  <button className="btn text" onClick={() => setActiveTab('invoices')}>View All</button>
                </div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Client</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.slice(0, 4).map(inv => (
                      <tr key={inv.id}>
                        <td>{inv.id}</td>
                        <td>{inv.client}</td>
                        <td>${inv.amount.toLocaleString()}</td>
                        <td>
                          <span 
                            className="status-badge"
                            style={{ 
                              backgroundColor: `${getStatusColor(inv.status)}20`, 
                              color: getStatusColor(inv.status) 
                            }}
                          >
                            {inv.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Tax Summary */}
              <div className="section-card">
                <div className="section-header">
                  <h3>Tax Withholdings</h3>
                  <span className="badge warning">Due {taxSummary.quarterlyDue}</span>
                </div>
                <div className="tax-summary">
                  <div className="tax-row">
                    <span className="tax-label">Federal</span>
                    <div className="tax-bar">
                      <div 
                        className="tax-paid" 
                        style={{ width: `${(taxSummary.federal.paid / taxSummary.federal.liability) * 100}%` }}
                      ></div>
                    </div>
                    <span className="tax-amount">${taxSummary.federal.due.toLocaleString()} due</span>
                  </div>
                  <div className="tax-row">
                    <span className="tax-label">State</span>
                    <div className="tax-bar">
                      <div 
                        className="tax-paid" 
                        style={{ width: `${(taxSummary.state.paid / taxSummary.state.liability) * 100}%` }}
                      ></div>
                    </div>
                    <span className="tax-amount">${taxSummary.state.due.toLocaleString()} due</span>
                  </div>
                  <div className="tax-row">
                    <span className="tax-label">FICA</span>
                    <div className="tax-bar">
                      <div 
                        className="tax-paid" 
                        style={{ width: `${(taxSummary.fica.paid / taxSummary.fica.liability) * 100}%` }}
                      ></div>
                    </div>
                    <span className="tax-amount">${taxSummary.fica.due.toLocaleString()} due</span>
                  </div>
                </div>
                <div className="tax-total">
                  <span>Total Due:</span>
                  <span className="total-amount">
                    ${(taxSummary.federal.due + taxSummary.state.due + taxSummary.fica.due).toLocaleString()}
                  </span>
                </div>
                <button className="btn primary full-width" disabled title="Coming soon">Make Quarterly Payment</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="invoices-content">
            <div className="filters">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
              <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
                <option value="all">All Clients</option>
                {clientOptions.map(client => (
                  <option key={client} value={client}>{client}</option>
                ))}
              </select>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Due Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                      No invoices match the selected filters
                    </td>
                  </tr>
                )}
                {filteredInvoices.map(inv => (
                  <tr key={inv.id}>
                    <td>{inv.id}</td>
                    <td>{inv.client}</td>
                    <td>{inv.date}</td>
                    <td>{inv.date}</td>
                    <td>${inv.amount.toLocaleString()}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor: `${getStatusColor(inv.status)}20`,
                          color: getStatusColor(inv.status)
                        }}
                      >
                        {inv.status}
                      </span>
                    </td>
                    <td>
                      <button className="btn small" onClick={() => setViewedInvoice(inv)}>View</button>
                      {inv.status === 'draft' && (
                        <button
                          className="btn small primary"
                          onClick={() => handleSendInvoice(inv)}
                          disabled={inv.dbId == null || sendingId === inv.id}
                          title={inv.dbId == null ? 'Sample data — sending is available for live invoices' : undefined}
                        >
                          {sendingId === inv.id ? 'Sending...' : 'Send'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="payments-content">
            <div className="empty-tab">Payment history is coming soon.</div>
          </div>
        )}

        {activeTab === 'payroll' && (
          <div className="payroll-content">
            <div className="payroll-header">
              <div className="next-payroll">
                <h3>Next Payroll Run</h3>
                <p className="payroll-date">{stats.payroll.nextRun}</p>
                <p className="payroll-amount">Estimated: ${stats.payroll.amount.toLocaleString()}</p>
              </div>
              <button className="btn primary large" disabled title="Coming soon">Run Payroll</button>
            </div>

            <div className="payroll-breakdown">
              <h4>Payroll Breakdown</h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Hours</th>
                    <th>Gross</th>
                    <th>Taxes</th>
                    <th>Net</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Sarah Chen</td>
                    <td><span className="badge">W2</span></td>
                    <td>80</td>
                    <td>$4,333</td>
                    <td>$1,300</td>
                    <td>$3,033</td>
                  </tr>
                  <tr>
                    <td>Mike Rodriguez</td>
                    <td><span className="badge secondary">1099</span></td>
                    <td>64</td>
                    <td>$2,880</td>
                    <td>-</td>
                    <td>$2,880</td>
                  </tr>
                  <tr>
                    <td>James Wilson</td>
                    <td><span className="badge secondary">1099</span></td>
                    <td>56</td>
                    <td>$1,960</td>
                    <td>-</td>
                    <td>$1,960</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}><strong>Total</strong></td>
                    <td><strong>$9,173</strong></td>
                    <td><strong>$1,300</strong></td>
                    <td><strong>$7,873</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'taxes' && (
          <div className="taxes-content">
            <div className="tax-alert">
              <span className="alert-icon">⚠️</span>
              <div className="alert-content">
                <strong>Quarterly taxes due {taxSummary.quarterlyDue}</strong>
                <p>Total estimated payment: ${(taxSummary.federal.due + taxSummary.state.due + taxSummary.fica.due).toLocaleString()}</p>
              </div>
              <button className="btn primary" disabled title="Coming soon">Make Payment</button>
            </div>

            <div className="tax-cards">
              <div className="tax-card">
                <h4>Federal Income Tax</h4>
                <div className="tax-details">
                  <div className="detail-row">
                    <span>Total Liability</span>
                    <span>${taxSummary.federal.liability.toLocaleString()}</span>
                  </div>
                  <div className="detail-row">
                    <span>Paid YTD</span>
                    <span className="positive">${taxSummary.federal.paid.toLocaleString()}</span>
                  </div>
                  <div className="detail-row total">
                    <span>Balance Due</span>
                    <span className="negative">${taxSummary.federal.due.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="tax-card">
                <h4>State Tax (PA)</h4>
                <div className="tax-details">
                  <div className="detail-row">
                    <span>Total Liability</span>
                    <span>${taxSummary.state.liability.toLocaleString()}</span>
                  </div>
                  <div className="detail-row">
                    <span>Paid YTD</span>
                    <span className="positive">${taxSummary.state.paid.toLocaleString()}</span>
                  </div>
                  <div className="detail-row total">
                    <span>Balance Due</span>
                    <span className="negative">${taxSummary.state.due.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="tax-card">
                <h4>FICA (Social Security + Medicare)</h4>
                <div className="tax-details">
                  <div className="detail-row">
                    <span>Total Liability</span>
                    <span>${taxSummary.fica.liability.toLocaleString()}</span>
                  </div>
                  <div className="detail-row">
                    <span>Paid YTD</span>
                    <span className="positive">${taxSummary.fica.paid.toLocaleString()}</span>
                  </div>
                  <div className="detail-row total">
                    <span>Balance Due</span>
                    <span className="negative">${taxSummary.fica.due.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Detail Modal */}
      {viewedInvoice && (
        <div className="modal-overlay" onClick={() => setViewedInvoice(null)}>
          <div className="invoice-modal" onClick={e => e.stopPropagation()}>
            <div className="section-header">
              <h3>Invoice {viewedInvoice.id}</h3>
              <button className="btn text" onClick={() => setViewedInvoice(null)}>Close</button>
            </div>
            <div className="tax-details">
              <div className="detail-row">
                <span>Client</span>
                <span>{viewedInvoice.client}</span>
              </div>
              <div className="detail-row">
                <span>Date</span>
                <span>{viewedInvoice.date}</span>
              </div>
              <div className="detail-row">
                <span>Amount</span>
                <span>${viewedInvoice.amount.toLocaleString()}</span>
              </div>
              <div className="detail-row total">
                <span>Status</span>
                <span
                  className="status-badge"
                  style={{
                    backgroundColor: `${getStatusColor(viewedInvoice.status)}20`,
                    color: getStatusColor(viewedInvoice.status)
                  }}
                >
                  {viewedInvoice.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .financials-dashboard {
          padding: 0;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          padding: 20px;
        }

        .stat-card.large {
          grid-column: span 1;
        }

        .stat-card.warning {
          border-color: var(--warning);
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .stat-icon {
          font-size: 24px;
        }

        .stat-trend {
          font-size: 12px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .stat-trend.up {
          background: rgba(16, 185, 129, 0.1);
          color: var(--success);
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 14px;
          color: var(--text-secondary);
        }

        .stat-secondary {
          font-size: 12px;
          color: var(--text-muted);
          margin-top: 8px;
        }

        .content-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 24px;
        }

        .section-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          padding: 20px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .section-header h3 {
          margin: 0;
          font-size: 16px;
        }

        .tax-summary {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 20px;
        }

        .tax-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .tax-label {
          width: 60px;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .tax-bar {
          flex: 1;
          height: 8px;
          background: var(--bg-tertiary, #334155);
          border-radius: 4px;
          overflow: hidden;
        }

        .tax-paid {
          height: 100%;
          background: var(--success);
          border-radius: 4px;
        }

        .tax-amount {
          font-size: 13px;
          color: var(--text-secondary);
          width: 80px;
          text-align: right;
        }

        .tax-total {
          display: flex;
          justify-content: space-between;
          padding-top: 16px;
          border-top: 1px solid var(--border-color, #334155);
          margin-bottom: 16px;
        }

        .total-amount {
          font-weight: 700;
          color: var(--warning);
        }

        .payroll-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: var(--bg-secondary);
          padding: 24px;
          border-radius: 12px;
          margin-bottom: 24px;
        }

        .payroll-date {
          font-size: 24px;
          font-weight: 700;
          margin: 8px 0 4px;
        }

        .payroll-amount {
          color: var(--text-secondary);
        }

        .tax-alert {
          display: flex;
          align-items: center;
          gap: 16px;
          background: rgba(245, 158, 11, 0.1);
          border: 1px solid var(--warning);
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 24px;
        }

        .alert-icon {
          font-size: 24px;
        }

        .alert-content {
          flex: 1;
        }

        .alert-content p {
          margin: 4px 0 0;
          color: var(--text-secondary);
        }

        .tax-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        .tax-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          padding: 20px;
        }

        .tax-card h4 {
          margin: 0 0 16px;
          font-size: 14px;
        }

        .tax-details {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          font-size: 14px;
        }

        .detail-row.total {
          padding-top: 12px;
          border-top: 1px solid var(--border-color, #334155);
          font-weight: 600;
        }

        .positive {
          color: var(--success);
        }

        .negative {
          color: var(--danger);
        }

        .badge.secondary {
          background: rgba(100, 116, 139, 0.2);
          color: var(--secondary);
        }

        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .invoice-modal {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          padding: 20px;
          width: 100%;
          max-width: 420px;
        }

        .empty-tab {
          padding: 40px;
          text-align: center;
          color: var(--text-secondary);
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
        }

        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .content-grid {
            grid-template-columns: 1fr;
          }

          .tax-cards {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default FinancialsDashboard;
